import {
  createFileRoute,
  Outlet,
  Link,
  useLocation,
  useNavigate,
  redirect,
} from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { cn } from "@/lib/utils";
import { apiGet } from "@/lib/api";
import { DateRangeFilter } from "@/components/reports/date-range-filter";
import { AdminMobileNav } from "@/components/admin/admin-mobile-nav";
import { MobileSectionSelect } from "@/components/layout/mobile-section-select";

const reportSearchSchema = z.object({
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  projectId: z.string().optional(),
});

export const Route = createFileRoute("/_authenticated/reports")({
  validateSearch: reportSearchSchema,
  beforeLoad: ({ context }) => {
    const user = (context as { user?: { role?: string | null } }).user;
    if (!["admin", "div_admin", "office_personnel"].includes(user?.role || "")) {
      throw redirect({ to: "/" });
    }
  },
  component: ReportsLayout,
});

const tabs = [
  { label: "Overview", to: "/reports" },
  { label: "Heatmap", to: "/reports/heatmap" },
  { label: "Hazards", to: "/reports/hazards" },
  { label: "High Risk", to: "/reports/high-risk" },
  { label: "Permits", to: "/reports/permits" },
  { label: "Subs", to: "/reports/subs" },
  { label: "Export", to: "/reports/export" },
] as const;

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function ReportsLayout() {
  const { user } = Route.useRouteContext();
  const adminNav = user?.role === "admin" || user?.role === "div_admin";
  const location = useLocation();
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();

  const dateFrom = search.dateFrom || daysAgo(30);
  const dateTo = search.dateTo || today();
  const projectId = search.projectId;

  // Fetch projects for the filter dropdown
  const { data: projectsData } = useQuery({
    queryKey: ["projects-list"],
    queryFn: () =>
      apiGet<{ id: string; name: string }[]>("/api/projects"),
    staleTime: 5 * 60_000,
  });

  const projects = Array.isArray(projectsData)
    ? projectsData
    : [];

  return (
    <div className="space-y-4 max-w-5xl mx-auto">
      {/* Admins reach Reports from the Admin list, so on a phone they keep that list at the top */}
      {adminNav && <AdminMobileNav userId={user?.id} isSuperAdmin={user?.role === "admin"} />}
      <h1 className={adminNav ? "hidden text-2xl font-bold text-text-primary pt-2 md:block" : "text-2xl font-bold text-text-primary pt-2"}>Reports</h1>

      {/* Filter bar */}
      <DateRangeFilter
        dateFrom={dateFrom}
        dateTo={dateTo}
        projectId={projectId}
        projects={projects}
        onFilterChange={(updates) => {
          navigate({
            search: (prev: Record<string, unknown>) => ({
              ...prev,
              ...updates,
            }),
            replace: true,
          });
        }}
      />

      {/* Phones: a drop-down list instead of a sideways-scrolling strip */}
      <MobileSectionSelect
        label="Report"
        value={tabs.filter((t) => (t.to === "/reports" ? location.pathname === "/reports" || location.pathname === "/reports/" : location.pathname.startsWith(t.to))).slice(-1)[0]?.to ?? "/reports"}
        options={tabs.map((t) => ({ value: t.to, label: t.label }))}
        onChange={(to) => navigate({ to, search })}
      />

      {/* Tab navigation */}
      <nav className="hidden gap-1 border-b overflow-x-auto scrollbar-hide md:flex">
        {tabs.map((tab) => {
          const isActive =
            tab.to === "/reports"
              ? location.pathname === "/reports" || location.pathname === "/reports/"
              : location.pathname.startsWith(tab.to);
          return (
            <Link
              key={tab.to}
              to={tab.to}
              search={search}
              className={cn(
                "px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors shrink-0 whitespace-nowrap",
                isActive
                  ? "border-paris-red text-paris-red-text"
                  : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
              )}
            >
              {tab.label}
            </Link>
          );
        })}
      </nav>

      {/* Child routes */}
      <Outlet />
    </div>
  );
}
